// CanvasResizeHandles.tsx
import React, { useCallback, useRef } from "react";
import { AspectRatio, CanvasResizeState, Rect, ResizeHandleType } from "#types/types";
import { useKernel } from "@/kernel/Kernel";
import {
  useKernelStore,
} from "@/kernel/kernelStores";
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';

interface CanvasResizeHandlesProps {
  workspaceId: string;
  isActive: boolean;
}

const EMPTY_RECT: Rect = { x: 0, y: 0, w: 0, h: 0 };

const DEFAULT_CANVAS_RESIZE_STATE: CanvasResizeState = {
  isResizing: false,
  handleType: null,
  startPos: null,
  initialRect: null,
  delta: { dx: 0, dy: 0 },
  isShiftPressed: false,
  workspaceId: null,
};

const CanvasResizeHandles = React.memo(({
    workspaceId,
    isActive
}: CanvasResizeHandlesProps) => {
  const { kernel } = useKernel();
  const projectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const activeTool = useKernelStore((state) => state.activeTool);
  const canvasResizeState = useKernelStore((state) => {
    const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
    return workspace?.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE;
  });
  const generationRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.generationRect ?? EMPTY_RECT
  );
  const frameRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect ?? EMPTY_RECT
  );
  const aspectRatio: AspectRatio = useKernelStore((state) => {
    const value = state.inventory.StudioWorkspace.registry[workspaceId]?.aspectRatio;
    return typeof value === 'number' ? value : null;
  });
  const animatedGenerationRect = useKernelStore((state) => {
    if (!isActive) return null;
    const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
    if (!workspace || workspace.projectId !== projectId) return null;
    return workspace.animatedGenerationRect ?? null;
  });
  const zoomLevel = useKernelStore((state) =>
    projectId
      ? (() => {
          const runtime = state.inventory.Project.registry[projectId]?.runtime;
          const value = runtime?.zoomLevel;
          return typeof value === 'number' && Number.isFinite(value) ? value : 1;
        })()
      : 1
  );

  const lastClickInfo = useRef<{ time: number, handle: ResizeHandleType } | null>(null);
  const initialResizeHandleSize = React.useMemo(() => 14 / zoomLevel, [zoomLevel]);
  const cornerSize = React.useMemo(() => 22 / zoomLevel, [zoomLevel]);
  const moveHandleThickness = React.useMemo(() => 40 / zoomLevel, [zoomLevel]);

  const getResizeHandleStyle = useCallback((handleType: ResizeHandleType): React.CSSProperties => {
    if (activeTool !== null || (canvasResizeState.isResizing && canvasResizeState.workspaceId === workspaceId)) { // Make handles non-interactive unless the neutral Select tool is active, and invisible during resize
      return { width: '0px', height: '0px', backgroundColor: 'transparent', cursor: 'default', pointerEvents: 'none' };
    }

    if (handleType === 'topRight') {
      return {
        top: `${(generationRect.y - cornerSize / 3)}px`,
        left: `${(generationRect.x + generationRect.w - 2 * cornerSize / 3)}px`,
        width: `${cornerSize}px`,
        height: `${cornerSize}px`
      };
    }
    if (handleType === 'bottomRight') {
      return {
        top: `${(generationRect.y + generationRect.h - 2 * cornerSize / 3)}px`,
        left: `${(generationRect.x + generationRect.w - 2 * cornerSize / 3)}px`,
        width: `${cornerSize}px`,
        height: `${cornerSize}px`
      };
    }
    if (handleType === 'bottomLeft') {
      return {
        top: `${(generationRect.y + generationRect.h - 2 * cornerSize / 3)}px`,
        left: `${(generationRect.x - cornerSize / 3)}px`,
        width: `${cornerSize}px`,
        height: `${cornerSize}px`
      };
    }
    if (handleType === 'topLeft') {
      return {
        top: `${(generationRect.y - cornerSize / 3)}px`,
        left: `${(generationRect.x - cornerSize / 3)}px`,
        width: `${cornerSize}px`,
        height: `${cornerSize}px`
      };
    }

    return handleType === 'left' || handleType === 'right' ?
      { height: `${generationRect.h}px`,
      top: `${generationRect.y}px`,
      width: `${initialResizeHandleSize}px`,
      left: handleType === 'left' ? `${(-initialResizeHandleSize + generationRect.x)}px` : `${(generationRect.w + generationRect.x)}px` } // Position corrected: no division by zoomLevel
    : { width: `${generationRect.w}px`,
      left: `${generationRect.x}px`,
      height: `${initialResizeHandleSize}px`,
      top: handleType === 'top' ? `${(-initialResizeHandleSize + generationRect.y)}px` : `${(generationRect.h + generationRect.y)}px` }; // Position corrected: no division by zoomLevel
  }, [canvasResizeState, initialResizeHandleSize, activeTool, generationRect, cornerSize, workspaceId]);

  const getMoveHandleStyle = useCallback((position: 'top' | 'bottom' | 'left' | 'right'): React.CSSProperties => {
    if (activeTool !== null || (canvasResizeState.isResizing && canvasResizeState.workspaceId === workspaceId)) {
      return { display: 'none', pointerEvents: 'none' };
    }

    const commonStyle: React.CSSProperties = {
      position: 'absolute',
      cursor: 'move',
      zIndex: 9,
    };

    switch (position) {
      case 'top':
        return {
          ...commonStyle,
          top: `${generationRect.y}px`,
          left: `${generationRect.x}px`,
          width: `${generationRect.w}px`,
          height: `${moveHandleThickness}px`,
        };
      case 'bottom':
        return {
          ...commonStyle,
          top: `${generationRect.y + generationRect.h - moveHandleThickness}px`,
          left: `${generationRect.x}px`,
          width: `${generationRect.w}px`,
          height: `${moveHandleThickness}px`,
        };
      case 'left':
        return {
          ...commonStyle,
          top: `${generationRect.y + moveHandleThickness}px`,
          left: `${generationRect.x}px`,
          width: `${moveHandleThickness}px`,
          height: `${generationRect.h - 2 * moveHandleThickness}px`,
        };
      case 'right':
        return {
          ...commonStyle,
          top: `${generationRect.y + moveHandleThickness}px`,
          left: `${generationRect.x + generationRect.w - moveHandleThickness}px`,
          width: `${moveHandleThickness}px`,
          height: `${generationRect.h - 2 * moveHandleThickness}px`,
        };
    }
  }, [activeTool, canvasResizeState, workspaceId, generationRect, moveHandleThickness]);


  const startCanvasResize = useCallback((handleType: ResizeHandleType) => (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (activeTool !== null) {
      return;
    }

    const isTouch = 'touches' in e;
    if (!isTouch && (e as React.MouseEvent).button !== 0) {
      return;
    }

    const clientX = (isTouch ? (e as React.TouchEvent).touches[0]?.clientX : (e as React.MouseEvent).clientX) || 0;
    const clientY = (isTouch ? (e as React.TouchEvent).touches[0]?.clientY : (e as React.MouseEvent).clientY) || 0;

    if (!isTouch) e.preventDefault();

    const now = Date.now();
    if (lastClickInfo.current && (now - lastClickInfo.current.time < 300) && lastClickInfo.current.handle === handleType) {
      lastClickInfo.current = null;
      e.stopPropagation();
      if (projectId) {
        kernel.current?.resizeGenerationAreaToWorkspace(projectId, workspaceId);
      }
      return;
    }

    lastClickInfo.current = { time: now, handle: handleType };

    if (!projectId) {
      return;
    }

    kernel.current?.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      isResizingCanvas: true,
      canvasResizeState: {
        isResizing: true,
        handleType: handleType,
        startPos: { x: clientX, y: clientY },
        initialRect: generationRect,
        delta: { dx: 0, dy: 0 },
        isShiftPressed: (e as React.MouseEvent).shiftKey || false,
        workspaceId: workspaceId,
      },
    }), projectId);
    e.stopPropagation();
  }, [activeTool, kernel, workspaceId, generationRect, projectId]);

  const handleMouseDownLeft = startCanvasResize('left');
  const handleMouseDownRight = startCanvasResize('right');
  const handleMouseDownTop = startCanvasResize('top');
  const handleMouseDownBottom = startCanvasResize('bottom');
  const handleMouseDownTopRight = startCanvasResize('topRight');
  const handleMouseDownBottomRight = startCanvasResize('bottomRight');
  const handleMouseDownBottomLeft = startCanvasResize('bottomLeft');
  const handleMouseDownTopLeft = startCanvasResize('topLeft');
  const handleMouseDownMove = startCanvasResize('move');

  const handleDoubleClick = useCallback(() => {
    if (activeTool !== null || !projectId) return;
    kernel.current?.resizeGenerationAreaToWorkspace(projectId, workspaceId);
  }, [activeTool, kernel, workspaceId, projectId]);

  const canvasBoundaryStyle = useCallback(() => {
    if (!kernel.current) return {};
    const scaledBorderSize = 2 / zoomLevel;

    // If this specific component's workspace is being resized, calculate the new rect
    if (canvasResizeState.isResizing && canvasResizeState.workspaceId === workspaceId) {
      const { w: newW, h: newH, x: newX, y: newY } = kernel.current.calculateNewRectForResize(canvasResizeState, generationRect, aspectRatio, frameRect);
      return {
        width: `${(newW + 2 * scaledBorderSize) * zoomLevel}px`,
        height: `${(newH + 2 * scaledBorderSize) * zoomLevel}px`,
        transform: `translate(${newX - scaledBorderSize}px, ${newY - scaledBorderSize}px) scale(${1.0 / zoomLevel})`,
        transformOrigin: 'top left',
      };
    }

    // Otherwise (not resizing, or another workspace is resizing), return the static position
    return {
      transform: `translate(${generationRect.x - scaledBorderSize}px, ${generationRect.y - scaledBorderSize}px) scale(${1.0 / zoomLevel})`,
      width: `${(generationRect.w + 2 * scaledBorderSize) * zoomLevel}px`,
      height: `${(generationRect.h + 2 * scaledBorderSize) * zoomLevel}px`
    };
  }, [kernel, canvasResizeState, generationRect, zoomLevel, aspectRatio, frameRect, workspaceId]);



  if (animatedGenerationRect !== null) {
    const scaledBorderSize = 2 / zoomLevel;
    return (
      <div
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          border: `2px dashed ${isActive ? 'white' : 'rgba(255, 255, 255, 0.25)'}`,
          borderRadius: '25px',
          boxSizing: 'border-box',
          zIndex: 1,
          transformOrigin: '0 0',
          transform: `translate(${animatedGenerationRect.x - scaledBorderSize}px, ${animatedGenerationRect.y - scaledBorderSize}px) scale(${1.0 / zoomLevel})`,
          width: `${(animatedGenerationRect.w + 2 * scaledBorderSize) * zoomLevel}px`,
          height: `${(animatedGenerationRect.h + 2 * scaledBorderSize) * zoomLevel}px`,
        }}
      ></div>
    );
  } else {
    return (
      <>
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            border: `2px dashed ${isActive ? 'white' : 'rgba(255, 255, 255, 0.25)'}`,
            borderRadius: '25px',
            boxSizing: 'border-box',
            zIndex: 1,
            transformOrigin: '0 0',
            ...canvasBoundaryStyle()
          }}
        ></div>
        <div style={getMoveHandleStyle('top')} onMouseDown={handleMouseDownMove} />
        <div style={getMoveHandleStyle('bottom')} onMouseDown={handleMouseDownMove} />
        <div style={getMoveHandleStyle('left')} onMouseDown={handleMouseDownMove} />
        <div style={getMoveHandleStyle('right')} onMouseDown={handleMouseDownMove} />
        <div
          className="absolute cursor-ew-resize z-10"
          style={{ ...getResizeHandleStyle('left') }}
          onMouseDown={handleMouseDownLeft}
          onTouchStart={handleMouseDownLeft}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-ew-resize z-10"
          style={{ ...getResizeHandleStyle('right') }}
          onMouseDown={handleMouseDownRight}
          onTouchStart={handleMouseDownRight}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-ns-resize z-10"
          style={{ ...getResizeHandleStyle('top') }}
          onMouseDown={handleMouseDownTop}
          onTouchStart={handleMouseDownTop}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-ns-resize z-10"
          style={{ ...getResizeHandleStyle('bottom') }}
          onMouseDown={handleMouseDownBottom}
          onTouchStart={handleMouseDownBottom}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-nesw-resize z-10"
          style={{ ...getResizeHandleStyle('topRight') }}
          onMouseDown={handleMouseDownTopRight}
          onTouchStart={handleMouseDownTopRight}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-nwse-resize z-10"
          style={{ ...getResizeHandleStyle('bottomRight') }}
          onMouseDown={handleMouseDownBottomRight}
          onTouchStart={handleMouseDownBottomRight}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-nesw-resize z-10"
          style={{ ...getResizeHandleStyle('bottomLeft') }}
          onMouseDown={handleMouseDownBottomLeft}
          onTouchStart={handleMouseDownBottomLeft}
          onDoubleClick={handleDoubleClick}
        ></div>
        <div
          className="absolute cursor-nwse-resize z-10"
          style={{ ...getResizeHandleStyle('topLeft') }}
          onMouseDown={handleMouseDownTopLeft}
          onTouchStart={handleMouseDownTopLeft}
          onDoubleClick={handleDoubleClick}
        ></div>
      </>
    );
  }
});

CanvasResizeHandles.displayName = 'CanvasResizeHandles';

export default CanvasResizeHandles;

